import React, { SyntheticEvent, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useQuery } from '@apollo/client';
import { PageTypes } from 'global';
import { connect } from 'react-redux';
import { CreditSubmission, Proposal } from '@trnsact/trnsact-shared-types';
import { CreditSubmissionProposalsTable } from 'modules/proposals/ui';
import ReSendProposalModal from '../../../pages/Proposals/ReSendProposalModal';
import { ProposalTable } from './table/ProposalTable';
import { TableFiltersPanel } from '../../Table/TableFiltersPanel';
import { proposalFiltersInitialValue } from '../constants';
import { ProposalListResponse, ProposalListVariables, ProposalTableFilters } from '../types';
import { GET_VENDOR_OPPORTUNITY_PROPOSALS } from '../queries';
import {
  filterTableDataByFiltersValues,
  getStatusUniqueOptions,
  mapProposalToVendorOpportunitiesTableData,
} from '../lib';
import { CardContainer } from '../../shared/CardContainer/CardContainer';
import { RenderWithCondition } from '../../shared/Conditional/RenderWithCondition';

interface Props {
  voId?: any;
  account: any;
  applicant?: any;
  cs: CreditSubmission;
  pageType: PageTypes;
}

const ProposalList = ({ cs, pageType, voId, account }: Props) => {
  const [proposalTableData, setProposalTableData] = useState<any[]>([]);
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null);

  const [tableFilters, setTableFilters] = useState<ProposalTableFilters>(proposalFiltersInitialValue);

  const [isReProposalModalOpen, setIsReSendProposalModalOpen] = useState<boolean>(false);

  const handleResend = (event: SyntheticEvent, proposal: Proposal) => {
    event.stopPropagation();

    setSelectedProposal(proposal);
    setIsReSendProposalModalOpen(true);
  };

  const { loading, error } = useQuery<ProposalListResponse, ProposalListVariables>(GET_VENDOR_OPPORTUNITY_PROPOSALS, {
    fetchPolicy: 'no-cache',
    context: { authRequired: true },
    notifyOnNetworkStatusChange: true,
    variables: { voId },
    onCompleted(data) {
      const mappedData = mapProposalToVendorOpportunitiesTableData(data?.proposals ?? []);
      setProposalTableData(mappedData);
    },
  });

  const filteredTableData = useMemo(
    () => filterTableDataByFiltersValues(proposalTableData, tableFilters),
    [proposalTableData, tableFilters]
  );

  if (error) {
    return (
      <Typography component="span" variant="h3">
        {String(error)}
      </Typography>
    );
  }

  return (
    <>
      <CardContainer sx={{ height: '100%' }}>
        <RenderWithCondition condition={pageType === PageTypes.CreditSubmission}>
          <CreditSubmissionProposalsTable creditSubmissionId={cs.creditSubmissionId!} accountId={account.id} />
        </RenderWithCondition>

        {pageType === PageTypes.VendorOpportunities && (
          <>
            <Box mb="1rem">
              <TableFiltersPanel
                options={{
                  status: { label: 'Status', values: getStatusUniqueOptions(proposalTableData ?? []) },
                }}
                onSearchChange={(filter, nextValue) => {
                  setTableFilters(prev => ({ ...prev, [filter]: nextValue }));
                }}
                onOptionsChange={(filter, nextValue) => {
                  setTableFilters(prev => ({ ...prev, [filter]: nextValue }));
                }}
              />
            </Box>

            <ProposalTable
              pageType={pageType}
              handleResend={handleResend}
              isTableDataLoading={loading}
              processedData={filteredTableData ?? []}
            />
          </>
        )}
      </CardContainer>

      {pageType === PageTypes.CreditSubmission && (
        <ReSendProposalModal
          open={isReProposalModalOpen}
          proposal={selectedProposal}
          proposalId={selectedProposal?.id}
          proposalEmail={selectedProposal?.email}
          proposalApplicant={selectedProposal?.fullName}
          handleClose={() => {
            setIsReSendProposalModalOpen(false);
          }}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: any) => ({
  vp: state.vp,
  account: state.account,
  refetch: state.refetch,
  userProfile: state.userProfile,
  creditSubmissions: state.creditSubmissions,
});

export default connect(mapStateToProps, null)(ProposalList);
